import type { PlacementGroup } from '@/modules/adv/ad-service'
import { PLAYGAMA_FALLBACK } from '@/modules/adv/header-bidding/prebid/bidders-config'
import { MINIPLAY_CLID } from '@/modules/main-frame/custom-integrations/miniplay'
import { type AdProvidersSettings, AdProviderTypes, type PlatformType, type EffectiveType } from '@/types'

import { getCommonProvidersAdSettings } from './common-providers-settings'
import { DEFAULT_AD_UNIT_TIMEOUT } from './constants'
import {
    createGoogleAdSettings,
    createGoogleInstreamSettings,
    createHeaderBiddingAdSettings,
} from './provider-factories'
import { filterAdProviderSettings, filterGoogleProviders } from './utils'

export type GamChildNetwork = { gamSubAccount: string; pageDomain: string }

function withGamChildNetwork(network: GamChildNetwork | null, providers: AdProvidersSettings[]): AdProvidersSettings[] {
    if (!network) {
        return providers
    }
    const { gamSubAccount, pageDomain } = network
    return providers.map((config) => {
        switch (config.type) {
            case AdProviderTypes.GAM_H5:
            case AdProviderTypes.GAM_BANNER_INTERSTITIAL:
                return { ...config, options: { ...config.options, gamSubAccount, pageDomain } }
            case AdProviderTypes.GAM_INSTREAM:
                return {
                    ...config,
                    options: {
                        ...config.options,
                        gamSubAccount,
                        pageURL: `https://${pageDomain}`,
                        useCurrentPageUrl: false,
                    },
                }
            default:
                return config
        }
    })
}

export const getPartnerAdSettings = ({
    gamAccount,
    platform,
    clid,
    effectiveType,
    isExportOnWidget,
    isGoogleProvidersEnabled,
    gamChildNetwork,
}: {
    gamAccount: string
    platform: PlatformType
    clid: string
    effectiveType: EffectiveType | undefined
    isExportOnWidget: boolean
    isGoogleProvidersEnabled: boolean
    gamChildNetwork: GamChildNetwork | null
}): PlacementGroup<AdProvidersSettings>[] => {
    const common = getCommonProvidersAdSettings(gamAccount, platform, true)

    const CUSTOM_PROVIDERS_BY_CLID: Partial<Record<string, AdProvidersSettings[]>> = {
        [MINIPLAY_CLID]: [
            common.GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
            createGoogleInstreamSettings(gamAccount, platform, {
                adUnitConfig: {
                    desktop: {
                        interstitial_preroll: 'instream_preroll_no_floor_desktop',
                        interstitial: 'instream_no_floor_desktop',
                        rewarded: 'instream_rewarded_no_floor_desktop',
                    },
                    mobile: {
                        interstitial_preroll: 'instream_preroll_no_floor_mobile',
                        interstitial: 'instream_no_floor_mobile',
                        rewarded: 'instream_rewarded_no_floor_mobile',
                    },
                },
            }),
        ],
    }

    const providers =
        CUSTOM_PROVIDERS_BY_CLID[clid] ||
        (isExportOnWidget
            ? [
                  createGoogleAdSettings(gamAccount, platform, {
                      adUnitConfig: {
                          desktop: {
                              rewarded: 'widget_rewarded_desktop',
                              interstitial: 'widget_h5_interstitial_desktop',
                              interstitial_preroll: 'widget_h5_interstitial_preroll_desktop',
                          },
                          mobile: {
                              rewarded: 'widget_rewarded_mobile',
                              interstitial: 'widget_h5_interstitial_mobile',
                              interstitial_preroll: 'widget_h5_interstitial_preroll_mobile',
                          },
                      },
                  }),
                  createGoogleInstreamSettings(gamAccount, platform, {
                      timeout: DEFAULT_AD_UNIT_TIMEOUT,
                      adUnitConfig: {
                          desktop: {
                              rewarded: 'widget_instream_rewarded_desktop',
                              interstitial: 'widget_instream_desktop',
                              interstitial_preroll: 'widget_instream_preroll_desktop',
                          },
                          mobile: {
                              rewarded: 'widget_instream_rewarded_mobile',
                              interstitial: 'widget_instream_mobile',
                              interstitial_preroll: 'widget_instream_preroll_mobile',
                          },
                      },
                  }),
                  common.GOOGLE_BANNER_GAME_PAGE_SETTINGS,
              ]
            : filterAdProviderSettings(effectiveType, [
                  common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT,
                  common.GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
                  common.GOOGLE_AD_GAME_PAGE_SETTINGS_INTERSTITIAL_FORMAT,
                  common.GOOGLE_BANNER_GAME_PAGE_SETTINGS,
              ]))

    const groups: PlacementGroup<AdProvidersSettings>[] = [
        {
            placements: ['*'],
            settings: filterGoogleProviders(isGoogleProvidersEnabled, [
                common.GOOGLE_AD_PORTAL_SETTINGS,
                common.HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS,
            ]),
        },
        {
            placements: ['interstitial', 'interstitial_midroll', 'interstitial_preroll', 'rewarded'],
            settings: [
                ...filterGoogleProviders(isGoogleProvidersEnabled, providers),
                createHeaderBiddingAdSettings(platform, [PLAYGAMA_FALLBACK]),
            ],
        },
    ]

    return groups.map((group) => ({ ...group, settings: withGamChildNetwork(gamChildNetwork, group.settings) }))
}
